import React, { useEffect } from 'react'
import { translate } from '../../selectors/translations'
import useSWR from "swr"
import { apiV2 } from "../../repositories/repository"
import {
  barChartConfig,
  lineChartConfig,
} from "../patients/PatientGameStats/PatientGameStatsCommon"
import { Chart } from "chart.js/auto"
import dayjs from "dayjs"

const Games: React.FC = () => {
  let {
    data: results,
    // eslint-disable-next-line prefer-const
    isLoading,
  } = useSWR(
    'api.game.getGeneralStats',
    () => apiV2.game.getGeneralStats(),
    {revalidateOnFocus: false}
  )

  useEffect(() => {
    if (!results) {
      return
    }
    const twoMonthsAgo = dayjs().subtract(2, 'months')
    results = {
      averagePerPatient: results.averagePerPatient,
      unique: results.unique.filter(e => dayjs(e.date).isAfter(twoMonthsAgo)),
      all: results.all.filter(e => dayjs(e.date).isAfter(twoMonthsAgo)),
    }
    const charts: Chart[] = []

    const gamesPerDate = (dates: string[], games: {
      date: string,
      amount: number
    }[]) => dates.map(date => games.find((e) => date === e.date)).map(e => e?.amount ?? 0)

    // @ts-ignore
    const uniqueDates: string[] = [...new Set(results.unique.map(e => e.date))]
    const uniqueData = {
      labels: uniqueDates.map((date) => date.substring(5)),
      datasets: [
        {
          label: 'Stroop',
          data: gamesPerDate(uniqueDates, results.unique.filter(e => e.kind === "stroop")),
          borderColor: '#8fbc6b',
          borderWidth: 2,
          backgroundColor: 'rgba(82, 158, 0, 0.15)',
          fill: true,
          pointStyle: false,
          pointRadius: 4,
          pointHoverRadius: 8,
          cubicInterpolationMode: 'monotone',
        },
        {
          label: 'N-Back',
          data: gamesPerDate(uniqueDates, results.unique.filter(e => e.kind === "nBack")),
          borderColor: '#6b78bc',
          borderWidth: 2,
          backgroundColor: 'rgba(0,21,158,0.15)',
          fill: true,
          pointStyle: false,
          pointRadius: 4,
          pointHoverRadius: 8,
          cubicInterpolationMode: 'monotone',
        },
        {
          label: 'Arithmetic',
          data: gamesPerDate(uniqueDates, results.unique.filter(e => e.kind === "arithmetic")),
          borderColor: '#bc6b6b',
          borderWidth: 2,
          backgroundColor: 'rgba(158,0,0,0.15)',
          fill: true,
          pointStyle: false,
          pointRadius: 4,
          pointHoverRadius: 8,
          cubicInterpolationMode: 'monotone',
        },
        {
          label: 'Fruit reaction',
          data: gamesPerDate(uniqueDates, results.unique.filter(e => e.kind === "fruitReaction")),
          borderColor: '#bb4fd3',
          borderWidth: 2,
          backgroundColor: 'rgba(113,0,158,0.15)',
          fill: true,
          pointStyle: false,
          pointRadius: 4,
          pointHoverRadius: 8,
          cubicInterpolationMode: 'monotone',
        },
      ]
    }
    const allData = {
      labels: uniqueDates.map((date) => date.substring(5)),
      datasets: [
        {
          label: 'Stroop',
          data: gamesPerDate(uniqueDates, results.all.filter(e => e.kind === "stroop")),
          borderColor: '#8fbc6b',
          borderWidth: 2,
          backgroundColor: 'rgba(82, 158, 0, 0.15)',
          fill: true,
          pointStyle: false,
          pointRadius: 4,
          pointHoverRadius: 8,
          cubicInterpolationMode: 'monotone',
        },
        {
          label: 'N-Back',
          data: gamesPerDate(uniqueDates, results.all.filter(e => e.kind === "nBack")),
          borderColor: '#6b78bc',
          borderWidth: 2,
          backgroundColor: 'rgba(0,21,158,0.15)',
          fill: true,
          pointStyle: false,
          pointRadius: 4,
          pointHoverRadius: 8,
          cubicInterpolationMode: 'monotone',
        },
        {
          label: 'Arithmetic',
          data: gamesPerDate(uniqueDates, results.all.filter(e => e.kind === "arithmetic")),
          borderColor: '#bc6b6b',
          borderWidth: 2,
          backgroundColor: 'rgba(158,0,0,0.15)',
          fill: true,
          pointStyle: false,
          pointRadius: 4,
          pointHoverRadius: 8,
          cubicInterpolationMode: 'monotone',
        },

        {
          label: 'Fruit reaction',
          data: gamesPerDate(uniqueDates, results.all.filter(e => e.kind === "fruitReaction")),
          borderColor: '#bb4fd3',
          borderWidth: 2,
          backgroundColor: 'rgba(113,0,158,0.15)',
          fill: true,
          pointStyle: false,
          pointRadius: 4,
          pointHoverRadius: 8,
          cubicInterpolationMode: 'monotone',
        },
      ]
    }
    const perPatientData = {
      labels: [''],
      datasets: [
        {
          label: 'Stroop',
          data: [results.averagePerPatient.find(e => e._id === 'stroop')?.averageSessions],
          borderColor: '#8fbc6b',
          borderWidth: 2,
          backgroundColor: '#8fbc6b',
          fill: true,
          pointStyle: false,
          pointRadius: 4,
          pointHoverRadius: 8,
          cubicInterpolationMode: 'monotone',
        },
        {
          label: 'N-Back',
          data: [results.averagePerPatient.find(e => e._id === 'nBack')?.averageSessions],
          borderColor: '#6b78bc',
          borderWidth: 2,
          backgroundColor: '#6b78bc',
          fill: true,
          pointStyle: false,
          pointRadius: 4,
          pointHoverRadius: 8,
          cubicInterpolationMode: 'monotone',
        },
        {
          label: 'Arithmetic',
          data: [results.averagePerPatient.find(e => e._id === 'arithmetic')?.averageSessions],
          borderColor: '#bc6b6b',
          borderWidth: 2,
          backgroundColor: '#bc6b6b',
          fill: true,
          pointStyle: false,
          pointRadius: 4,
          pointHoverRadius: 8,
          cubicInterpolationMode: 'monotone',
        },
        {
          label: 'Fruit reaction',
          data: [results.averagePerPatient.find(e => e._id === 'fruitReaction')?.averageSessions],
          borderColor: '#bb4fd3',
          borderWidth: 2,
          backgroundColor: 'rgba(113,0,158,0.5)',
          fill: true,
          pointStyle: false,
          pointRadius: 4,
          pointHoverRadius: 8,
          cubicInterpolationMode: 'monotone',
        },
      ]
    }
    const totalPlayersData = {
      labels: [''],
      datasets: [
        {
          label: 'Stroop',
          data: [results.averagePerPatient.find(e => e._id === 'stroop')?.playersCount],
          borderColor: '#8fbc6b',
          backgroundColor: '#8fbc6b',
          fill: true,
          pointStyle: false,
          pointRadius: 4,
          pointHoverRadius: 8,
          cubicInterpolationMode: 'monotone',
        },
        {
          label: 'N-Back',
          data: [results.averagePerPatient.find(e => e._id === 'nBack')?.playersCount],
          borderColor: '#6b78bc',
          backgroundColor: '#6b78bc',
          fill: true,
          pointStyle: false,
          pointRadius: 4,
          pointHoverRadius: 8,
          cubicInterpolationMode: 'monotone',
        },
        {
          label: 'Arithmetic',
          data: [results.averagePerPatient.find(e => e._id === 'arithmetic')?.playersCount],
          borderColor: '#bc6b6b',
          backgroundColor: '#bc6b6b',
          fill: true,
          pointStyle: false,
          pointRadius: 4,
          pointHoverRadius: 8,
          cubicInterpolationMode: 'monotone',
        },
        {
          label: 'Fruit reaction',
          data: [results.averagePerPatient.find(e => e._id === 'fruitReaction')?.playersCount],
          borderColor: '#bb4fd3',
          backgroundColor: 'rgba(113,0,158,0.5)',
          fill: true,
          pointStyle: false,
          pointRadius: 4,
          pointHoverRadius: 8,
          cubicInterpolationMode: 'monotone',
        },
      ]
    }
    const totalSessionsData = {
      labels: [''],
      datasets: [
        {
          label: 'Stroop',
          data: [results.averagePerPatient.find(e => e._id === 'stroop')?.totalSessions],
          borderColor: '#8fbc6b',
          backgroundColor: '#8fbc6b',
          fill: true,
          pointStyle: false,
          pointRadius: 4,
          pointHoverRadius: 8,
          cubicInterpolationMode: 'monotone',
        },
        {
          label: 'N-Back',
          data: [results.averagePerPatient.find(e => e._id === 'nBack')?.totalSessions],
          borderColor: '#6b78bc',
          backgroundColor: '#6b78bc',
          fill: true,
          pointStyle: false,
          pointRadius: 4,
          pointHoverRadius: 8,
          cubicInterpolationMode: 'monotone',
        },
        {
          label: 'Arithmetic',
          data: [results.averagePerPatient.find(e => e._id === 'arithmetic')?.totalSessions],
          borderColor: '#bc6b6b',
          backgroundColor: '#bc6b6b',
          fill: true,
          pointStyle: false,
          pointRadius: 4,
          pointHoverRadius: 8,
          cubicInterpolationMode: 'monotone',
        },
        {
          label: 'Fruit reaction',
          data: [results.averagePerPatient.find(e => e._id === 'fruitReaction')?.totalSessions],
          borderColor: '#bb4fd3',
          backgroundColor: 'rgba(113,0,158,0.5)',
          fill: true,
          pointStyle: false,
          pointRadius: 4,
          pointHoverRadius: 8,
          cubicInterpolationMode: 'monotone',
        },
      ]
    }

    const uniqueChartCtx = document.getElementById(`unique-games`)
    if (uniqueChartCtx) {
      // @ts-ignore
      charts.push(new Chart(uniqueChartCtx, {
        ...lineChartConfig(),
        data: uniqueData
      }))
    }
    const allChartCtx = document.getElementById(`all-games`)
    if (allChartCtx) {
      // @ts-ignore
      charts.push(new Chart(allChartCtx, {
        ...lineChartConfig(),
        data: allData
      }))
    }
    const averageChartCtx = document.getElementById(`average-per-patient-games`)
    if (averageChartCtx) {
      // @ts-ignore
      charts.push(new Chart(averageChartCtx, {
        ...barChartConfig,
        data: perPatientData
      }))
    }
    const totalPlayersChartCtx = document.getElementById(`total-players-count`)
    if (totalPlayersChartCtx) {
      // @ts-ignore
      charts.push(new Chart(totalPlayersChartCtx, {
        ...barChartConfig,
        data: totalPlayersData
      }))
    }
    const totalSessionsChartCtx = document.getElementById(`total-sessions`)
    if (totalSessionsChartCtx) {
      // @ts-ignore
      charts.push(new Chart(totalSessionsChartCtx, {
        ...barChartConfig,
        data: totalSessionsData
      }))
    }

    return () => {
      charts.map(c => c.destroy())
    }
  }, [results])

  if (isLoading) {
    return (
      <div className="flex w-100 h-100 justify-center align-center">
        <p className="m-0">{translate('Loading...')}</p>
      </div>
    )
  }

  return (<>
    <div className="card-box">
      <h2>Unique play sessions (1 per patient per day)</h2>
      <canvas id="unique-games" style={{maxHeight: '50vh'}}></canvas>
    </div>
    <div className="card-box">
      <h2>All play sessions</h2>
      <canvas id="all-games" style={{maxHeight: '50vh'}}></canvas>
    </div>
    <div className="card-box">
      <h2>Average amount of play sessions per patient</h2>
      <canvas id="average-per-patient-games" style={{maxHeight: '50vh'}}></canvas>
    </div>
    <div className="card-box">
      <h2>Total players count</h2>
      <canvas id="total-players-count" style={{maxHeight: '50vh'}}></canvas>
    </div>
    <div className="card-box">
      <h2>Total amount of sessions</h2>
      <canvas id="total-sessions" style={{maxHeight: '50vh'}}></canvas>
    </div>
  </>)
}

export default Games
